import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
    ORDER_TYPES,
    PRODUCT_TYPES_STOCKS,
    PRODUCT_TYPES_FUTURES,
    removeLeadingZeros,
    VALIDITY_TYPES,
    getSellQty
} from "@/lib/placeOrder";
import { cn, putComma } from "@/lib/utils";
import { updateField, setError, updateProductType } from "@/store/features/placeOrderSlice";
import { useDispatch, useSelector } from "react-redux";
import disabled from "@/assets/disabled.png";
import { GoChevronDown } from "react-icons/go";
import { AiFillInfoCircle } from "react-icons/ai";
import { Switch } from "../ui/switch";
import { CiSquareMinus, CiSquarePlus } from "react-icons/ci";
import { MdColorize } from "react-icons/md";
import SetGTTInfo from "./SetGTTInfo";
import MarketDepth from "./MarketDepth";
import TradeCardBF from "./TradeCardBF";
import PlaceOrderValidator from "@/lib/PlaceOrderValidator";
import { useEffect } from "react";
import { Mixpanel } from "@/elements/Mixpanel";
import { getInstrument } from "@/lib/tradeCards";

export default function PlaceOrderMain() {
    const dispatch = useDispatch();
    const validator = new PlaceOrderValidator(dispatch);

    const {
        exchange,
        productType,
        isMoreOpen,
        order_type,
        quantity,
        type,
        stockDetails,
        isNfo,
        disc_quantity,
        validity,
        trigger_price,
        isPlusSLMOpen,
        tradeCardData,
        ltp,
        price,
        error,
        shouldPlaceGtt,
        stopLoss,
        targetPrice,
        scriptData,
        uckt,
        lckt,
        source,
        orderMargin
    } = useSelector((state) => state.placeOrder);

    useEffect(() => {
        if (tradeCardData?.id) {
            dispatch(
                updateField({
                    field: "shouldPlaceGtt",
                    value: productType === "I" || (type === "SELL" && !isNfo) ? false : true
                })
            );
        }
    }, [productType]);

    useEffect(() => {
        if (!shouldPlaceGtt) {
            dispatch(setError({ stopLoss: null, targetPrice: null }));
        }
    }, [shouldPlaceGtt]);

    useEffect(() => {
        if (type === "SELL" && !isNfo) {
            dispatch(updateField({ field: "shouldPlaceGtt", value: false }));
            dispatch(setError({ stopLoss: null, targetPrice: null }));
        }

        dispatch(setError({ price: null }));

        if (order_type?.code === "MKT" || order_type?.code === "LMT") {
            dispatch(setError({ trigger_price: null }));
        }
    }, [type, order_type?.code]);

    useEffect(() => {
        Mixpanel.pageView({
            page: "order_form",
            symbol: stockDetails?.nseSymbol ?? stockDetails?.bseSymbol,
            expiry_date: null,
            stock_name: stockDetails?.compName,
            trade_idea: tradeCardData?.id ? true : false,
            instrument_name: getInstrument(stockDetails?.nseSymbol ?? stockDetails?.bseSymbol),
            product_type: productType == "I" ? "intraday" : productType == "M" ? "overnight" : "delivery",
            exchange: isNfo ? "NFO" : exchange,
            gtt_status:
                targetPrice && stopLoss && stopLoss != "0"
                    ? "sl_tgt"
                    : targetPrice && targetPrice != "0"
                    ? "tgt"
                    : stopLoss && stopLoss != "0"
                    ? "sl"
                    : "off",
            buy_sell_order_type: type,
            source: source
        });
    }, []);

    const sellQty = getSellQty(productType, orderMargin);

    return (
        <Tabs
            defaultValue={productType}
            className="w-full rounded-none h-full overflow-y-auto select-none pb-24"
            value={productType}
            onValueChange={(value) => {
                dispatch(updateProductType(value));
                Mixpanel.track("tab_clicked", {
                    page: "order_form",
                    tab: value == "I" ? "intraday" : "delivery"
                });
            }}
        >
            <TabsList className="grid w-full grid-cols-2 px-0 py-1 sticky top-0 bg-white z-[2]">
                {(isNfo ? PRODUCT_TYPES_FUTURES : PRODUCT_TYPES_STOCKS).map((ele) => (
                    <TabsTrigger key={ele.code} value={ele.code}>
                        {ele.text
                            ?.replace("?", `${putComma(stockDetails?.leverage ?? 0.0, 1)}x`)
                            ?.replace("(0.0x)", "")}
                    </TabsTrigger>
                ))}
            </TabsList>
            {(isNfo ? PRODUCT_TYPES_FUTURES : PRODUCT_TYPES_STOCKS).map((ele) => (
                <TabsContent
                    key={ele.code}
                    value={ele.code}
                    className="bg-[#F2F2F2] mt-0 p-4 min-h-[calc(100%-222px)] overflow-y-auto pb-24"
                >
                    <div className="bg-white rounded-xl p-3 pb-4">
                        <div className="flex justify-start items-center gap-2">
                            {ORDER_TYPES?.slice(0, isPlusSLMOpen ? 4 : 2).map((OT) => (
                                <button
                                    disabled={OT.disableOnValidity === validity?.code}
                                    key={OT.code}
                                    style={{ "--image-url": `url(${disabled.src})` }}
                                    onClick={() => {
                                        dispatch(updateField({ field: "order_type", value: OT }));
                                        Mixpanel.track("chip_clicked", {
                                            page: "order_form",
                                            chip_text: OT?.text,
                                            symbol: stockDetails?.nseSymbol ?? stockDetails?.bseSymbol,
                                            expiry_date: null
                                        });
                                    }}
                                    className={cn(
                                        "select-none rounded-lg border-[1.5px] w-1/4 text-center text-xs py-1.5 duration-300 ease-linear border-[#9D9D9D] text-[#747474] font-medium bg-white disabled:bg-[#bcbcbc18] disabled:border-[#BCBCBC] disabled:text-[#BCBCBC] disabled:bg-[image:var(--image-url)] disabled:bg-cover hover:bg-[#ecf6fe90]",
                                        {
                                            "border-primary text-primary font-bold bg-[#ECF6FE]":
                                                OT.code === order_type.code
                                        }
                                    )}
                                >
                                    {OT.text}
                                </button>
                            ))}
                            {!isPlusSLMOpen && (
                                <>
                                    <button
                                        disabled={ORDER_TYPES?.[2]?.disableOnValidity === validity?.code}
                                        className="text-xs text-primary font-bold w-1/4 text-left disabled:text-[#BCBCBC]"
                                        onClick={() => {
                                            dispatch(updateField({ field: "order_type", value: ORDER_TYPES[2] }));
                                            dispatch(updateField({ field: "isPlusSLMOpen", value: true }));
                                            Mixpanel.track("cta_clicked", {
                                                page: "order_form",
                                                cta_text: "sl_label_click"
                                            });
                                        }}
                                    >
                                        + SL/M
                                    </button>
                                    <button className="w-1/4" />
                                </>
                            )}
                        </div>

                        <div className="font-Inter flex flex-row justify-center w-full gap-4">
                            <div className="w-full mt-6 relative">
                                <span className="absolute -top-1 left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white">
                                    Price
                                </span>
                                <input
                                    onClick={() => {
                                        if (order_type.disablePrice) {
                                            dispatch(
                                                updateField({
                                                    field: "order_type",
                                                    value: order_type.code === "MKT" ? ORDER_TYPES[1] : ORDER_TYPES[2]
                                                })
                                            );
                                            Mixpanel.track("cta_clicked", {
                                                page: "order_form",
                                                cta_text: "sl_enabled"
                                            });
                                        }
                                    }}
                                    style={{ "--image-url": `url(${disabled.src})` }}
                                    type="text"
                                    className={cn(
                                        `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC]`,
                                        {
                                            "border-[#EB4E2C] focus:border-[#EB4E2C]": error.price,
                                            " bg-[#bcbcbc18] border-[#BCBCBC] text-[#bcbcbc0a] bg-[image:var(--image-url)] bg-cover":
                                                order_type.disablePrice
                                        }
                                    )}
                                    value={price}
                                    onChange={(e) => {
                                        const price = e.target.value;
                                        dispatch(updateField({ field: "price", value: removeLeadingZeros(price) }));
                                        validator.validateTriggerPrice(trigger_price, price, order_type, type, ltp);
                                        validator.validatePrice(
                                            price,
                                            trigger_price,
                                            order_type,
                                            uckt,
                                            lckt,
                                            orderMargin?.responseMap?.tickSize,
                                            type
                                        );
                                    }}
                                />
                                <ErrorMessage error={!order_type.disablePrice && error.price} />
                            </div>
                            {isNfo ? (
                                <div className={`w-full mt-6 relative`}>
                                    <div className="absolute -top-[7px] left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white">
                                        Qty. <span className="text-[#747474]">(Lot size: {stockDetails?.lotSize})</span>
                                    </div>
                                    <div
                                        className={`border p-1 w-full rounded-lg text-sm font-semibold text-[#414141] border-[#0862BC] flex justify-between items-center`}
                                    >
                                        <CiSquareMinus
                                            className="text-[#0862BC] cursor-pointer"
                                            size={32}
                                            onClick={() => {
                                                dispatch(
                                                    updateField({
                                                        field: "quantity",
                                                        value: quantity > 1 ? `${parseInt(quantity) - 1}` : "1"
                                                    })
                                                );
                                                Mixpanel.track("cta_clicked", {
                                                    page: "order_form",
                                                    chip_type: "multiplier"
                                                });
                                            }}
                                        />
                                        <input
                                            className="text-base font-bold text-black max-w-[50px] text-center"
                                            value={quantity}
                                            onChange={(e) => {
                                                const quantity = e.target.value;
                                                dispatch(
                                                    updateField({
                                                        field: "quantity",
                                                        value: removeLeadingZeros(quantity)
                                                    })
                                                );
                                                validator.validateQuantity(quantity, disc_quantity, type, sellQty);
                                            }}
                                        />
                                        <CiSquarePlus
                                            className="text-[#0862BC] cursor-pointer"
                                            size={32}
                                            onClick={() => {
                                                dispatch(
                                                    updateField({
                                                        field: "quantity",
                                                        value: `${parseInt(quantity) + 1}`
                                                    })
                                                );
                                                Mixpanel.track("cta_clicked", {
                                                    page: "order_form",
                                                    chip_type: "multiplier"
                                                });
                                            }}
                                        />
                                    </div>
                                    {!error.quantity ? (
                                        ((sellQty > 0 && type === "SELL") || (sellQty < 0 && type === "BUY")) && (
                                            <div className="mt-1 text-[10px] flex justify-end items-center gap-1 ml-auto">
                                                Available Qty:{" "}
                                                <span className="font-bold">{putComma(sellQty ?? "0", 0)} </span>
                                            </div>
                                        )
                                    ) : (
                                        <ErrorMessage error={error.quantity} />
                                    )}
                                </div>
                            ) : (
                                <div className="w-full mt-6 relative">
                                    <span className="absolute -top-1 left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white ">
                                        Quantity
                                    </span>
                                    <input
                                        type="text"
                                        className={cn(
                                            `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC]`,
                                            { "border-[#EB4E2C] focus:border-[#EB4E2C]": error.quantity }
                                        )}
                                        pattern="[0-9]*"
                                        step="0.01"
                                        inputMode="numeric"
                                        value={quantity}
                                        onChange={(e) => {
                                            const quantity = removeLeadingZeros(e.target.value || "");
                                            dispatch(updateField({ field: "quantity", value: quantity }));
                                            validator.validateQuantity(quantity, disc_quantity, type, sellQty);
                                        }}
                                    />
                                    {!error.quantity ? (
                                        sellQty > 0 &&
                                        type === "SELL" && (
                                            <div className=" text-[12px] pt-2 flex justify-end items-center gap-1 ml-auto">
                                                Available Qty:{" "}
                                                <span className="font-bold text-[12px]">
                                                    {putComma(sellQty ?? "0", 0)}{" "}
                                                </span>
                                            </div>
                                        )
                                    ) : (
                                        <ErrorMessage error={error.quantity} />
                                    )}
                                </div>
                            )}
                        </div>
                        <div
                            className={cn("font-Inter flex flex-row justify-start w-full mt-6 transition-all", {
                                "mt-0": !order_type.showTrigger
                            })}
                        >
                            <div
                                className={cn(`w-1/2 relative pr-2 max-h-[200px] overflow-visible opacity-100`, {
                                    "max-h-0 overflow-hidden opacity-0": !order_type.showTrigger
                                })}
                            >
                                <span className="absolute -top-1 left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white">
                                    Trigger Price
                                </span>
                                <input
                                    type="text"
                                    className={cn(
                                        `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC]`,
                                        { "border-[#EB4E2C] focus:border-[#EB4E2C]": error.trigger_price }
                                    )}
                                    value={trigger_price}
                                    onChange={(e) => {
                                        const trigger_price = e.target.value;
                                        dispatch(
                                            updateField({
                                                field: "trigger_price",
                                                value: removeLeadingZeros(trigger_price)
                                            })
                                        );
                                        validator.validateTriggerPrice(trigger_price, price, order_type, type, ltp);
                                    }}
                                />
                                <ErrorMessage error={error.trigger_price} />
                            </div>
                        </div>

                        <div className={cn("w-full ")}>
                            <hr className="mt-6" />
                            <div
                                className={cn(
                                    "font-Inter flex flex-row justify-start items-center w-full mt-6 gap-3 transition-all",
                                    {
                                        "grayscale opacity-50 blur-[0.1px]":
                                            productType === "I" || (type === "SELL" && !isNfo)
                                    }
                                )}
                            >
                                <div className="text-sm font-semibold">
                                    Set GTT <span className="text-[10px] font-medium">(Target - Stoploss)</span>
                                </div>
                                <SetGTTInfo />
                                <Switch
                                    className="w-8 h-5 data-[state=checked]:bg-primary ml-auto"
                                    ballClassName="w-4 h-4 data-[state=checked]:translate-x-[12px]"
                                    onCheckedChange={(shouldPlaceGtt) => {
                                        if (!(productType === "I" || (type === "SELL" && !isNfo))) {
                                            dispatch(updateField({ field: "shouldPlaceGtt", value: shouldPlaceGtt }));
                                        }
                                        Mixpanel.track("toggle_clicked", {
                                            page: "order_form",
                                            toggle_switch: shouldPlaceGtt ? "yes" : "no",
                                            toggle_type: "GTT"
                                        });
                                    }}
                                    checked={shouldPlaceGtt}
                                />
                            </div>
                            <div
                                className={cn(
                                    "font-Inter flex flex-row justify-center w-full max-h-44 gap-4 transition-all overflow-hidden",
                                    { "pt-0 pb-0 max-h-0": !shouldPlaceGtt }
                                )}
                            >
                                <div className="w-full mt-6 relative">
                                    <span className="absolute -top-1 left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white">
                                        Stoploss Price
                                    </span>
                                    <input
                                        type="text"
                                        className={cn(
                                            `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC] disabled:bg-[#bcbcbc18] disabled:border-[#BCBCBC] disabled:text-[#bcbcbc0a] disabled:bg-[image:var(--image-url)] disabled:bg-cover`,
                                            { "border-[#EB4E2C] focus:border-[#EB4E2C]": error.stopLoss }
                                        )}
                                        value={stopLoss}
                                        onChange={(e) => {
                                            const stopLoss = e.target.value;
                                            dispatch(
                                                updateField({ field: "stopLoss", value: removeLeadingZeros(stopLoss) })
                                            );
                                            validator.validateStopLoss(stopLoss, price, type, shouldPlaceGtt);
                                        }}
                                    />
                                    {error.stopLoss ? (
                                        <ErrorMessage error={error.stopLoss} />
                                    ) : (
                                        stopLoss && (
                                            <p className="text-[10px] text-[#414141] text-right mt-1 font-semibold">
                                                <span
                                                    className={
                                                        stopLoss - price > 0
                                                            ? "text-[#00A300] mr-1"
                                                            : "text-[#EB4E2C] mr-1"
                                                    }
                                                >
                                                    {putComma(((stopLoss - price) / price) * 100)}%
                                                </span>
                                                from current price
                                            </p>
                                        )
                                    )}
                                </div>
                                <div className="w-full mt-6 relative">
                                    <span className="absolute -top-1 left-2.5 px-2 block text-[10px] leading-[10px] font-semibold text-black bg-white ">
                                        Target Price
                                    </span>
                                    <input
                                        type="text"
                                        className={cn(
                                            `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC]`,
                                            { "border-[#EB4E2C] focus:border-[#EB4E2C]": error.targetPrice }
                                        )}
                                        pattern="[0-9]*"
                                        step="0.01"
                                        inputMode="numeric"
                                        value={targetPrice}
                                        onChange={(e) => {
                                            const targetPrice = e.target.value;
                                            dispatch(
                                                updateField({
                                                    field: "targetPrice",
                                                    value: removeLeadingZeros(targetPrice)
                                                })
                                            );
                                            validator.validateTargetPrice(targetPrice, price, type, shouldPlaceGtt);
                                        }}
                                    />
                                    {error.targetPrice ? (
                                        <ErrorMessage error={error.targetPrice} />
                                    ) : (
                                        targetPrice && (
                                            <p className="text-[10px] text-[#414141] text-right mt-1 font-semibold">
                                                <span
                                                    className={
                                                        targetPrice - price > 0
                                                            ? "text-[#00A300] mr-1"
                                                            : "text-[#EB4E2C] mr-1"
                                                    }
                                                >
                                                    {putComma(((targetPrice - price) / price) * 100)}%
                                                </span>
                                                from current price
                                            </p>
                                        )
                                    )}
                                </div>
                            </div>
                        </div>

                        <hr className="my-6" />
                        <div className="flex items-center justify-between">
                            <MarketDepth source={"order_form"} scriptCode={scriptData?.scriptCode?.toString()}>
                                <div className="text-xs font-semibold text-primary/80 flex items-center gap-1 cursor-pointer">
                                    <MdColorize size={14} />
                                    View depth
                                </div>
                            </MarketDepth>
                            {tradeCardData?.id && (
                                <TradeCardBF
                                    tradeId={tradeCardData?.id}
                                    stockDetails={stockDetails}
                                    pageName={"order_form"}
                                />
                            )}
                        </div>

                        <div
                            className={cn("w-full max-h-96 overflow-hidden transition-all", { "max-h-0": !isMoreOpen })}
                        >
                            <hr className="mt-6" />
                            <div className="font-Inter flex flex-row justify-start items-center w-full mt-6 gap-3">
                                <div className={`w-full text-sm font-semibold`}>Validity</div>
                                {VALIDITY_TYPES.map((item, index) => (
                                    <div
                                        key={index}
                                        onClick={() => {
                                            dispatch(updateField({ field: "validity", value: item }));
                                            Mixpanel.track("chip_clicked", {
                                                page: "order_form",
                                                chip_text: item?.label
                                            });
                                            if (order_type.code === ORDER_TYPES[3].code) {
                                                dispatch(updateField({ field: "order_type", value: ORDER_TYPES[0] }));
                                            } else if (order_type.code === ORDER_TYPES[2].code) {
                                                dispatch(updateField({ field: "order_type", value: ORDER_TYPES[1] }));
                                            }
                                        }}
                                        className={cn(
                                            `w-full transition-all text-center text-sm font-medium border-[1.5px] p-1 rounded-lg cursor-pointer hover:bg-[#ecf6fe90] `,
                                            {
                                                "text-primary border-primary bg-[#ECF6FE] font-semibold":
                                                    validity.code === item.code
                                            }
                                        )}
                                    >
                                        {item.label}
                                    </div>
                                ))}
                            </div>
                            <div className="font-Inter flex flex-row justify-start items-start w-full mt-6">
                                <div className={`w-1/2 text-sm font-semibold mt-2.5`}>Disclose Qty.</div>
                                <div className={`w-[calc(50%-8px)] ml-auto relative duration-300 ease-linear`}>
                                    <input
                                        disabled={isNfo || validity.disableDisclose}
                                        style={{ "--image-url": `url(${disabled.src})` }}
                                        type="text"
                                        className={cn(
                                            `border-[1.5px] border-[#9D9D9D] px-4 py-2.5 w-full rounded-lg text-sm font-semibold text-[#414141] focus:border-[#0862BC] disabled:bg-[#bcbcbc18] disabled:border-[#BCBCBC] disabled:text-[#bcbcbc0a] disabled:bg-[image:var(--image-url)] disabled:bg-cover`,
                                            { "border-[#EB4E2C] focus:border-[#EB4E2C]": error.disc_quantity }
                                        )}
                                        value={disc_quantity}
                                        onChange={(e) => {
                                            const disc_quantity = e.target.value;
                                            dispatch(
                                                updateField({
                                                    field: "disc_quantity",
                                                    value: removeLeadingZeros(disc_quantity)
                                                })
                                            );
                                            validator.validateDiscQuantity(
                                                disc_quantity,
                                                quantity,
                                                isNfo || validity.disableDisclose || !isMoreOpen
                                            );
                                        }}
                                    />
                                    <ErrorMessage error={error.disc_quantity} />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className="w-full flex items-center justify-center mt-8 mb-4 gap-1 transition-all cursor-pointer"
                        onClick={() => {
                            dispatch(updateField({ field: "isMoreOpen", value: !isMoreOpen }));
                            Mixpanel.track("cta_clicked", {
                                page: "order_form",
                                cta_text: isMoreOpen ? "view_less" : "view_more"
                            });
                        }}
                    >
                        <div className="text-sm font-semibold">{isMoreOpen ? "View less" : "View more"}</div>
                        <GoChevronDown className={cn("transition-all", isMoreOpen && "rotate-180")} />
                    </div>
                </TabsContent>
            ))}
        </Tabs>
    );
}

export function ErrorMessage({ error, className }) {
    if (error) {
        return (
            <div className="flex ml-1 mt-1 items-start">
                <AiFillInfoCircle color="#EB4E2C" className={cn("text-[12px] mr-1 mt-0.5", className)} />
                <div className={cn("text-[10px] text-[#EB4E2C] ", className)}>{error}</div>
            </div>
        );
    }
}
